import React, {
  useContext,
  useCallback,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import CommentTiptapEditor from '@wix/da-editor-tiptap/pkg/components/Editor/presets/Comment';
import { UseEditorOptions } from '@wix/da-editor-tiptap/pkg/hooks/useEditor';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { EditorContext } from '@wix/da-editor-tiptap/pkg/components/Editor/Context';
import ExclusiveMaskIcon from '../CommentBox/lego/CommentBoxContentWrapper/ExclusiveMaskIcon';
import useEditorNg from '../DaEditorNg/hooks/useEditorNg';
import EditorCommon from '../DaEditorNg/EditorCommon';
import { CommentEditorContext } from './context';
import { CommentEditorSource } from '../CommentEditor/redux/types';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import cn from 'classnames';
import ChooseAvatarModal from '../CommentBox/lego/ChooseAvatarModal';
import { ExclusiveAvatarWithPopover } from '../Comment/lego/ExclusiveAvatar';
import { extractExclusiveAvatarDeviation } from '../Comment/utils';
import { useDispatch, useSelector } from 'react-redux';
import { getCompliantLocalStorageItem } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/selectors';
import { CompliantLocalStorageKeys } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/constants';
import {
  removeCompliantLocalStorageItem,
  setCompliantLocalStorageItem,
} from '@wix/da-react-context/pkg/compliantLocalStorage/redux/actions';
import { PapiDeviation } from '@wix/da-papi-types';
import s from './CommentEditor.scss';

export interface Props {
  className?: string;
  placeholder?: string;
  onChange?: UseEditorOptions['onEditorChange'];
  loadingIndicator?: React.ReactNode;
  onEditorReady?: UseEditorOptions['onEditorReady'];
  // TODO: Remove? Integrate the following?
  onFocus?: UseEditorOptions['onFocus'];
  onIconClick?: () => void;
}

const CommentEditor: React.FC<Props> = React.memo(props => {
  const {
    onChange,
    className,
    loadingIndicator,
    placeholder,
    onEditorReady,
    onIconClick,
  } = props;
  const { t } = useTranslation();
  const { initialContent, editorSource, rootElementRef } =
    useContext(CommentEditorContext);
  const { relevantComment } = useContext(CommentEditorContext);
  const exclusive_avatars = useFlag('exclusive_avatars');
  const { isExpanded } = useContext(CommentEditorContext);
  const [isChooseAvatarModalOpen, setIsChooseAvatarModalOpen] = useState(false);
  const { ref: editor, isReady: isEditorReady } = useContext(EditorContext);
  const dispatch = useDispatch();
  const lastExclusiveRaw = useSelector(state =>
    getCompliantLocalStorageItem(
      state as any,
      CompliantLocalStorageKeys.EXCLUSIVE_AVATAR_LAST_SELECTED
    )
  );

  // Track an avatar injected while editing so we can show it immediately
  const [injectedExclusiveAvatar, setInjectedExclusiveAvatar] = useState<
    PapiDeviation | undefined
  >(undefined);

  // When editing an existing comment, extract and show the exclusive avatar like read mode
  const editModeExclusiveAvatar = useMemo(() => {
    if (injectedExclusiveAvatar) return injectedExclusiveAvatar;
    // Only extract from the existing comment when we are actually editing
    if (editorSource !== CommentEditorSource.Edit) return undefined;
    return relevantComment
      ? extractExclusiveAvatarDeviation(relevantComment as any)
      : undefined;
  }, [editorSource, relevantComment, injectedExclusiveAvatar]);

  const { editorProps, editorSharedProps, editorCommonProps } = useEditorNg({
    onEditorChange: onChange,
    onEditorReady: onEditorReady,
  });

  // Restore last selected exclusive avatar from localStorage on mount (new comments only)
  useEffect(() => {
    if (!exclusive_avatars) return;
    if (editorSource === CommentEditorSource.Edit) return; // don't override when editing an existing comment
    if (injectedExclusiveAvatar) return; // already have a selection in this session
    try {
      if (!lastExclusiveRaw) return;
      const parsed = JSON.parse(lastExclusiveRaw) as PapiDeviation | null;
      if (parsed && (parsed as any).deviationId) {
        setInjectedExclusiveAvatar(parsed);
      }
    } catch (e) {}
  }, [
    exclusive_avatars,
    editorSource,
    injectedExclusiveAvatar,
    lastExclusiveRaw,
  ]);

  // (moved) Injection occurs at submit time in useCommentPostAction

  const handleIconClick = useCallback(() => {
    setIsChooseAvatarModalOpen(true);
    onIconClick && onIconClick();
  }, [onIconClick]);

  const focusEditor = useCallback(() => {
    if (editor && isEditorReady) {
      // Use setTimeout to ensure the modal has fully closed before focusing
      setTimeout(() => {
        editor.commands.focus();
      }, 100);
    }
  }, [editor, isEditorReady]);

  const handleCloseModal = useCallback(() => {
    setIsChooseAvatarModalOpen(false);
    focusEditor();
  }, [focusEditor]);

  const handleSelectAvatar = useCallback(
    (selectedDeviation: PapiDeviation | null) => {
      if (selectedDeviation === null) {
        try {
          dispatch(
            removeCompliantLocalStorageItem(
              CompliantLocalStorageKeys.EXCLUSIVE_AVATAR_LAST_SELECTED
            )
          );
        } catch (e) {}
        setInjectedExclusiveAvatar(undefined);
        if (editor && isEditorReady) {
          try {
            // Remove all da-exclusive-avatar nodes from the document safely
            const { state, view } = editor as any;
            const tr = state.tr;
            const ranges: Array<{ from: number; to: number }> = [];
            state.doc.descendants((node: any, pos: number) => {
              if (node?.type?.name === 'da-exclusive-avatar') {
                ranges.push({ from: pos, to: pos + node.nodeSize });
              }
            });
            ranges
              .sort((a, b) => b.from - a.from)
              .forEach(({ from, to }) => tr.delete(from, to));
            if (tr.docChanged) {
              view.dispatch(tr);
            }
          } catch (e) {}
        }
        setIsChooseAvatarModalOpen(false);
        focusEditor();
        return;
      }

      try {
        dispatch(
          setCompliantLocalStorageItem({
            key: CompliantLocalStorageKeys.EXCLUSIVE_AVATAR_LAST_SELECTED,
            value: JSON.stringify(selectedDeviation),
          })
        );
      } catch (e) {}

      setInjectedExclusiveAvatar(selectedDeviation || undefined);
      setIsChooseAvatarModalOpen(false);
      focusEditor();
    },
    [editor, isEditorReady, focusEditor, dispatch]
  );

  const placeholderText =
    placeholder ??
    (editorSource === CommentEditorSource.Reply
      ? t('commentbox.editor.placeholder.reply')
      : t('commentbox.editor.placeholder'));

  return (
    <ErrorBoundary debugComponent="CommentEditor">
      <EditorCommon {...editorCommonProps} />
      <div
        className={cn(
          s['commentEditorWrapper'],
          exclusive_avatars && s['exclusive-avatars'],
          className
        )}
      >
        {exclusive_avatars && editModeExclusiveAvatar && (
          <ExclusiveAvatarWithPopover
            deviation={editModeExclusiveAvatar}
            className={s['exclusive-avatar']}
            onAvatarClick={handleIconClick}
            avatarAriaLabel={t(
              'commentBox.exclusiveAvatars.changeAvatar',
              'Change avatar'
            )}
          />
        )}
        <CommentTiptapEditor
          {...editorSharedProps}
          {...editorProps}
          rootElementRef={rootElementRef as any}
          className={s['commentEditor']}
          placeholder={placeholderText}
          cookieType={undefined}
          loadingIndicator={loadingIndicator}
          initialContent={initialContent}
        />
        {exclusive_avatars && isExpanded && (
          <ExclusiveMaskIcon
            className={cn(
              s['exclusive-avatar-button'],
              editorSource === CommentEditorSource.Edit &&
                s['exclusive-avatar-button-editing']
            )}
            onClick={handleIconClick}
          />
        )}
      </div>
      {isChooseAvatarModalOpen && (
        <ChooseAvatarModal
          onClose={handleCloseModal}
          onSelect={handleSelectAvatar}
          currentlySelectedDeviation={editModeExclusiveAvatar ?? null}
        />
      )}
    </ErrorBoundary>
  );
});
CommentEditor.displayName = 'SharedCommentEditor';

export default CommentEditor;
